import React from "react";
import { Box, ButtonBase, Stack, Typography, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import LocalGasStationRoundedIcon from "@mui/icons-material/LocalGasStationRounded";
import { IBotData } from "../../shared/types";
import {
  hasBalancingActivity,
  hasLowGasActivity,
  hasWaitingForFundsActivity,
} from "../../utils/botUtils";
import { ChainAvatar, chainName } from "../ui/ChainAvatar";
import { StatusDot } from "../ui/StatusPill";
import { aggregateReturn, botState, gasSeverity } from "../../utils/botMetrics";
import { formatSignedPercent } from "../../utils/formatters";
import { tokens } from "../../theme";

interface BotNavListProps {
  groupedBotsByChain: Record<string, string[]>;
  groupedBots: Record<string, string[]>;
  botsData?: Record<string, IBotData>;
  /** Selected family; `null` = Overview, `undefined` = nothing (another section is open). */
  selected: string | null | undefined;
  onSelect: (family: string | null) => void;
  /**
   * Touch device (phones, iPad). Rows stay 40px there too, so iPad landscape (834px tall)
   * shows every family and the section rows without scrolling; full-width rows 2px apart
   * still give a comfortable target.
   */
  touch?: boolean;
  /** Larger type for the full-width phone drawer. */
  large?: boolean;
}

const NavItem: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  large?: boolean;
}> = ({ selected, onClick, children, ariaLabel, large = false }) => {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={selected ? "page" : undefined}
      aria-label={ariaLabel}
      sx={{
        width: "100%",
        justifyContent: "flex-start",
        gap: 1,
        px: 1.25,
        // Sidebar: 40px (44 on touch); the phone sheet: 48px rows.
        minHeight: large ? 48 : 40,
        "@media (pointer: coarse)": { minHeight: large ? 48 : 44 },
        borderRadius: "14px",
        color: selected ? "#111" : "text.primary",
        background: selected ? tokens.goldGradient : "transparent",
        boxShadow: selected ? "inset 0 1px 0 rgba(255,255,255,0.35)" : "none",
        fontWeight: selected ? 700 : 500,
        transition: "background-color 140ms ease, color 140ms ease",
        "&:hover": {
          bgcolor: selected ? undefined : "rgba(255,255,255,0.06)",
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${alpha(theme.palette.secondary.main, 0.7)}`,
          outlineOffset: 1,
        },
      }}
    >
      {children}
    </ButtonBase>
  );
};

/** Chain-grouped strategy navigation used by the sidebar and the mobile drawer. */
export const BotNavList: React.FC<BotNavListProps> = ({
  groupedBotsByChain,
  groupedBots,
  botsData,
  selected,
  onSelect,
  large = false,
}) => {
  const theme = useTheme();
  const chains = Object.keys(groupedBotsByChain).sort();
  const totalFamilies = chains.reduce((acc, c) => acc + groupedBotsByChain[c].length, 0);
  const fontSize = large ? "0.9375rem" : "0.875rem";

  return (
    <Stack
      component="div"
      // Touch rows are taller: tighter chain groups keep all families visible on an 834px-tall iPad.
      sx={{ gap: 1.25, "@media (pointer: coarse)": { gap: large ? 1.25 : 0.5 } }}
    >
      <NavItem selected={selected === null} onClick={() => onSelect(null)} large={large}>
        <SpaceDashboardRoundedIcon sx={{ fontSize: 18, color: selected === null ? "inherit" : "text.secondary" }} />
        <Typography component="span" sx={{ fontSize, fontWeight: "inherit" }}>
          Overview
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          component="span"
          sx={{ fontSize: "0.75rem", fontWeight: 650, color: selected === null ? alpha("#000", 0.55) : "text.secondary" }}
        >
          {totalFamilies}
        </Typography>
      </NavItem>

      {chains.map((chain) => (
        <Box key={chain} component="section" aria-label={chainName(chain)}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, mb: 0.5, "@media (pointer: coarse)": { mb: 0.25 } }}>
            <ChainAvatar chain={chain} size={18} />
            <Typography
              sx={{ fontSize: "0.75rem", fontWeight: 650, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}
            >
              {chainName(chain)}
            </Typography>
          </Stack>
          <Stack sx={{ gap: 0.25, "@media (pointer: coarse)": { gap: large ? 0.25 : 0 } }}>
            {[...groupedBotsByChain[chain]].sort().map((family) => {
              const isSelected = selected === family;
              const isLowGas = hasLowGasActivity(family, botsData);
              const isWaitingFunds = hasWaitingForFundsActivity(family, botsData);
              const familyIds = groupedBots[family] ?? [];
              const stats = familyIds.map((id) => botsData?.[id]?.stats).filter(Boolean) as IBotData["stats"][];
              const states = stats.map(botState);
              const outOfRange = states.includes("out-of-range");
              const idle = states.includes("idle");
              const balancing = states.includes("balancing") || hasBalancingActivity(family, botsData);
              const gasTone = stats.some((s) => s.isLowGas && gasSeverity(s) === "error") ? "error" : "warning";
              // Same % as the strategy card: P&L over basis, bots with a positive basis only.
              const pct = aggregateReturn(stats);
              const dotTone = outOfRange || idle ? "error" : balancing ? "brand" : "success";
              const dotLabel = outOfRange
                ? "A bot is out of range"
                : idle
                ? "A bot has no position"
                : balancing
                ? "Balancing in progress"
                : "All bots in range";
              const label = family.replace(/^.*?_/, "").split("_").join(" / ");
              const iconColor = isSelected ? "inherit" : undefined;

              return (
                <NavItem
                  key={family}
                  selected={isSelected}
                  onClick={() => onSelect(family)}
                  ariaLabel={family}
                  large={large}
                >
                  <Tooltip title={dotLabel} placement="right">
                    <Box sx={{ width: 14, display: "grid", placeItems: "center", flexShrink: 0 }}>
                      {isSelected ? (
                        // On the gold selected row: a solid dark disc carrying the filled status dot
                        // (a ring would read as the hollow "pending" dot).
                        <Box
                          component="span"
                          sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#111", display: "grid", placeItems: "center" }}
                        >
                          <StatusDot tone={dotTone} size={6} />
                        </Box>
                      ) : (
                        <StatusDot tone={dotTone} size={7} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography component="span" noWrap sx={{ fontSize, fontWeight: "inherit", minWidth: 0, textAlign: "left" }}>
                    {label}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {isLowGas && (
                    <Tooltip title="Low gas balance" placement="right">
                      <LocalGasStationRoundedIcon sx={{ fontSize: 15, color: iconColor ?? theme.palette[gasTone].main }} />
                    </Tooltip>
                  )}
                  {isWaitingFunds && (
                    <Tooltip title="Waiting for external funds" placement="right">
                      <HourglassBottomRoundedIcon sx={{ fontSize: 15, color: iconColor ?? theme.palette.warning.main }} />
                    </Tooltip>
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 650,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: isSelected
                        ? alpha("#000", 0.62)
                        : pct < 0
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                    }}
                  >
                    {formatSignedPercent(pct)}
                  </Typography>
                </NavItem>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default BotNavList;
